import React, { useMemo } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { HighlightOffRounded } from '@material-ui/icons';
import { DocumentTemplateType, ProjectArchiveSummary } from '../../api-client/autogenerated';
import DocumentTypeTreeView, {
  ALL_DOCUMENT_TYPES,
  FILE_MANAGER_DOCUMENT_TYPE,
} from '../custom-components/DocumentTypeTreeView';
import { PACKAGE_DOCUMENT_TYPES } from '../custom-components/document-type-utils';
import { CancelButton } from '../custom-components/CustomButtons';
import { parseDate } from '../../scripts/utils';

type ExportContentsDialogProps = {
  archive?: ProjectArchiveSummary;
  closeDialog: () => void;
};

const useStyles = makeStyles(() =>
  createStyles({
    titleContainer: {
      display: 'flex',
      flexDirection: 'row',
      height: '50px',
      padding: '8px 16px 8px 16px',
      borderBottom: '1px solid #EDECEC',
      minWidth: 500,
    },
    title: {
      marginTop: '3px',
      marginLeft: '6px',
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '22px',
      lineHeight: '30px',
      textAlign: 'center',
      color: '#0947B9',
      textTransform: 'uppercase',
      justifySelf: 'center',
    },
    rootIconButton: {
      padding: 0,
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
  }),
);

// Everything a new export could have been given, so an export taken before the picker existed --
// which contains the lot -- shows every box ticked.
const EVERYTHING = ALL_DOCUMENT_TYPES.filter(
  (type) => !PACKAGE_DOCUMENT_TYPES.includes(type as DocumentTemplateType),
);

/**
 * What a past export contains, shown in the same tree the export was picked with so the two read
 * the same way. Nothing here can be changed -- re-running an export means starting a new one.
 */
export default function ExportContentsDialog(props: ExportContentsDialogProps) {
  const { archive, closeDialog } = props;
  const classes = useStyles();

  const selectedDocumentTypes = useMemo<(DocumentTemplateType | string)[]>(() => {
    if (!archive?.documentTypes) return EVERYTHING;
    const documentTypes: (DocumentTemplateType | string)[] = [...archive.documentTypes];
    if (archive.includeDesignFiles) documentTypes.push(FILE_MANAGER_DOCUMENT_TYPE);
    return documentTypes;
  }, [archive]);

  const requestedOn =
    archive?.requestedOn && parseDate(archive.requestedOn).format('MM/DD/YYYY h:mm A');

  return (
    <Dialog open={!!archive} onClose={closeDialog} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.titleContainer}>
        <Typography className={classes.title} align="center">
          Export Contents
        </Typography>
        <IconButton
          id="export-contents-dialog-close-button"
          style={{ right: '20px', top: '12px', position: 'absolute' }}
          onClick={closeDialog}
          classes={{ root: classes.rootIconButton }}
        >
          <HighlightOffRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography
          style={{ marginTop: 8, marginBottom: 16, fontWeight: 'bolder', color: 'black' }}
        >
          {requestedOn}
        </Typography>
        <DocumentTypeTreeView
          selectedDocumentTypes={selectedDocumentTypes}
          // Read-only, so nothing ever calls these.
          setSelectedDocumentTypes={() => {}}
          includeEmails={!archive?.documentTypes || archive.includeEmails}
          setIncludeEmails={() => {}}
          hiddenDocumentTypes={PACKAGE_DOCUMENT_TYPES}
          disabled
        />
      </DialogContent>
      <DialogActions style={{ justifyContent: 'flex-end', padding: 16 }}>
        <CancelButton onClick={closeDialog}>Close</CancelButton>
      </DialogActions>
    </Dialog>
  );
}
